import { handleActions } from 'redux-actions';

import { createSelector } from '@reduxjs/toolkit';
import {
  createLoadByIdReducer,
  createLoadListReducerWithBegin,
  createOperationStateReducer,
} from '../../utils/getCrudConfig';
import {
  CREATE_CALENDAR_DATE,
  CREATE_DAILY_TARIFF,
  EDIT_CALENDAR_DATE,
  EDIT_DAILY_TARIFF,
  CALCULATE_TARIFF_COST,
  DELETE_CALENDAR_DATE,
  DELETE_DAILY_TARIFF,
  LOAD_CALENDAR_DATE_BY_ID,
  LOAD_CALENDAR_DATE_LIST,
  LOAD_DAILY_TARIFF_BY_ID,
  LOAD_DAILY_TARIFF_LIST,
  CREATE_TARIFF,
  DELETE_TARIFF,
  EDIT_TARIFF,
  LOAD_TARIFF_BY_ID,
  LOAD_TARIFF_LIST,
  LOAD_LOST_TICKET_LIST,
  CREATE_LOST_TICKET,
  EDIT_LOST_TICKET,
  DELETE_LOST_TICKET,
} from './actions';
import { TARIFF_MODULE } from '../../configs/moduleNames';

const initialState = {
  tariffList: [],
  tariffListMeta: {},
  loadingTariffList: false,
  currentTariff: null,
  loadingCurrentTariff: false,
  loadingCreateTariff: false,
  loadingEditTariff: false,
  loadingDeleteTariff: false,
  loadingTariffCostCalculation: false,

  calendarDateList: [],
  calendarDateListMeta: {},
  loadingCalendarDateList: false,
  currentCalendarDate: null,
  loadingCurrentCalendarDate: false,
  loadingCreateCalendarDate: false,
  loadingEditCalendarDate: false,
  loadingDeleteCalendarDate: false,

  dailyTariffList: [],
  dailyTariffListMeta: {},
  loadingDailyTariffList: false,
  currentDailyTariff: null,
  loadingCurrentDailyTariff: false,
  loadingCreateDailyTariff: false,
  loadingEditDailyTariff: false,
  loadingDeleteDailyTariff: false,

  lostTicketList: [],
  lostTicketListMeta: {},
  loadingLostTicketList: false,
  loadingCreateLostTicket: false,
  loadingEditLostTicket: false,
  loadingDeleteLostTicket: false,
};

const reducer = handleActions({
  ...createLoadListReducerWithBegin(TARIFF_MODULE + LOAD_TARIFF_LIST, 'tariffList', 'loadingTariffList'),
  ...createLoadByIdReducer(TARIFF_MODULE + LOAD_TARIFF_BY_ID, 'currentTariff', 'loadingCurrentTariff'),
  ...createOperationStateReducer(TARIFF_MODULE + CREATE_TARIFF, 'loadingCreateTariff'),
  ...createOperationStateReducer(TARIFF_MODULE + EDIT_TARIFF, 'loadingEditTariff'),
  ...createOperationStateReducer(TARIFF_MODULE + DELETE_TARIFF, 'loadingDeleteTariff'),
  ...createOperationStateReducer(TARIFF_MODULE + CALCULATE_TARIFF_COST, 'loadingTariffCostCalculation'),

  ...createLoadListReducerWithBegin(TARIFF_MODULE + LOAD_CALENDAR_DATE_LIST, 'calendarDateList', 'loadingCalendarDateList'),
  ...createLoadByIdReducer(TARIFF_MODULE + LOAD_CALENDAR_DATE_BY_ID, 'currentCalendarDate', 'loadingCurrentCalendarDate'),
  ...createOperationStateReducer(TARIFF_MODULE + CREATE_CALENDAR_DATE, 'loadingCreateCalendarDate'),
  ...createOperationStateReducer(TARIFF_MODULE + EDIT_CALENDAR_DATE, 'loadingEditCalendarDate'),
  ...createOperationStateReducer(TARIFF_MODULE + DELETE_CALENDAR_DATE, 'loadingDeleteCalendarDate'),

  ...createLoadListReducerWithBegin(TARIFF_MODULE + LOAD_DAILY_TARIFF_LIST, 'dailyTariffList', 'loadingDailyTariffList'),
  ...createLoadByIdReducer(TARIFF_MODULE + LOAD_DAILY_TARIFF_BY_ID, 'currentDailyTariff', 'loadingCurrentDailyTariff'),
  ...createOperationStateReducer(TARIFF_MODULE + CREATE_DAILY_TARIFF, 'loadingCreateDailyTariff'),
  ...createOperationStateReducer(TARIFF_MODULE + EDIT_DAILY_TARIFF, 'loadingEditDailyTariff'),
  ...createOperationStateReducer(TARIFF_MODULE + DELETE_DAILY_TARIFF, 'loadingDeleteDailyTariff'),

  ...createLoadListReducerWithBegin(TARIFF_MODULE + LOAD_LOST_TICKET_LIST, 'lostTicketList', 'loadingLostTicketList'),
  ...createOperationStateReducer(TARIFF_MODULE + CREATE_LOST_TICKET, 'loadingCreateLostTicket'),
  ...createOperationStateReducer(TARIFF_MODULE + EDIT_LOST_TICKET, 'loadingEditLostTicket'),
  ...createOperationStateReducer(TARIFF_MODULE + DELETE_LOST_TICKET, 'loadingDeleteLostTicket'),
}, initialState);

export default reducer;

export const getTariffData = createSelector(
  [state => state[TARIFF_MODULE]],
  state => {
    const {
      tariffList,
      tariffListMeta,
      loadingTariffList,
      currentTariff,
      loadingCurrentTariff,
      loadingCreateTariff,
      loadingEditTariff,
      loadingDeleteTariff,
      loadingTariffCostCalculation,
      calendarDateList,
      calendarDateListMeta,
      loadingCalendarDateList,
      currentCalendarDate,
      loadingCurrentCalendarDate,
      loadingCreateCalendarDate,
      loadingEditCalendarDate,
      loadingDeleteCalendarDate,
      dailyTariffList,
      dailyTariffListMeta,
      loadingDailyTariffList,
      currentDailyTariff,
      loadingCurrentDailyTariff,
      loadingCreateDailyTariff,
      loadingEditDailyTariff,
      loadingDeleteDailyTariff,
      lostTicketList,
      lostTicketListMeta,
      loadingLostTicketList,
      loadingCreateLostTicket,
      loadingEditLostTicket,
      loadingDeleteLostTicket,
    } = state;

    return {
      tariffList,
      tariffListMeta,
      loadingTariffList,
      currentTariff,
      loadingCurrentTariff,
      loadingCreateTariff,
      loadingEditTariff,
      loadingDeleteTariff,
      loadingTariffCostCalculation,
      calendarDateList,
      calendarDateListMeta,
      loadingCalendarDateList,
      currentCalendarDate,
      loadingCurrentCalendarDate,
      loadingCreateCalendarDate,
      loadingEditCalendarDate,
      loadingDeleteCalendarDate,
      dailyTariffList,
      dailyTariffListMeta,
      loadingDailyTariffList,
      currentDailyTariff,
      loadingCurrentDailyTariff,
      loadingCreateDailyTariff,
      loadingEditDailyTariff,
      loadingDeleteDailyTariff,
      lostTicketList,
      lostTicketListMeta,
      loadingLostTicketList,
      loadingCreateLostTicket,
      loadingEditLostTicket,
      loadingDeleteLostTicket,
    };
  },
);
